import { motion } from 'framer-motion';
import { X, Check, Instagram, Mail } from 'lucide-react';
import { TEAM, ASSETS, FLAVOR_LIST, CONTACTS } from '../lib/products';
import { useWaitlist } from '../context/WaitlistContext';
import KrispButton from '../components/KrispButton';
import Marquee from '../components/Marquee';
import Footer from '../sections/Footer';
import FloatingChips from '../components/FloatingChips';

const PROBLEMS = ['Regular chips: fried in palm oil, 5g protein, zero purpose.', 'Protein bars taste like cardboard and cost like gold.', '"Healthy" snacks are boring, beige and tiny.', 'You train hard. Nothing on the shelf was built for after.'];
const SOLUTIONS = ['15G plant protein in every 50g pack — real crunch, real progress.', 'Roasted, never fried. No palm oil. No artificial colours.', 'Multigrain base. Two flavours that slap: Masala Krunch, Cheese & Herbs.', 'Flavour #3 chosen by you — vote for Salt & Pepper, Tomato, Sweet Chilli or Pudina.'];
const STATS = [['15G', 'PROTEIN / PACK'], ['0', 'PALM OIL. EVER.'], ['214', 'TEST BATCHES'], ['6', 'HUMANS. ONE CRUNCH.']];
const VALUES = [['LOUD', 'If it doesn’t crunch, it doesn’t ship.'], ['HONEST', 'Real macros on the front. Not hidden in 6pt type.'], ['ROASTED', 'Never fried. Never palm oil. Never artificial colours.'], ['ATHLETE FIRST', 'Built with gym rats, gamers and students in the room.']];

const Section = ({ id, kicker, title, children, className = '' }) => (
  <section id={id} className={`max-w-[1400px] mx-auto px-5 md:px-14 py-16 scroll-mt-6 ${className}`}>
    <p className="font-cond text-xs tracking-[0.4em] text-cocoa/60">{kicker}</p>
    <h2 className="font-display text-6xl md:text-8xl text-cocoa leading-none mt-1">{title}</h2>
    {children}
  </section>
);

export default function AboutPage() {
  const { openWaitlist } = useWaitlist();
  return (
    <main data-testid="about-page" className="bg-cream min-h-screen">
      <section className="relative overflow-hidden bg-kyellow">
        <div className="absolute -top-24 -left-24 w-[40vw] h-[40vw] rounded-full bg-cream/50" />
        <FloatingChips count={4} />
        <div className="relative max-w-[1500px] mx-auto px-6 md:px-14 py-16 md:py-24 grid md:grid-cols-[3fr_2fr] gap-10 items-center">
          <div>
            <p className="font-cond text-xl tracking-[0.3em] text-cocoa/70">ABOUT PROKRISP</p>
            <h1 className="font-display text-[17vw] md:text-[8vw] leading-[0.85] text-cocoa mt-2">WE MAKE CHIPS<br />THAT PULL<br />THEIR WEIGHT.</h1>
            <p className="text-base text-cocoa/80 mt-5 max-w-lg">PROKRISP is a six-person crew from India building snacks for people who go beyond: roasted multigrain chips with 15g protein that taste like a cheat day and read like a training plan.</p>
          </div>
          <motion.div
            className="w-72 md:w-96 mx-auto rounded-[2rem] bg-cocoa border-4 border-cocoa shadow-[10px_10px_0_#7A1E1E] p-8 flex flex-col items-center">
            <img src={ASSETS.logo} alt="PROKRISP" className="w-full object-contain" />
            <span className="font-cond text-sm tracking-[0.6em] text-kyellow mt-4">GO BEYOND</span>
          </motion.div>
        </div>
      </section>
      <Marquee dark text="GO BEYOND · REAL INGREDIENTS. REAL PROGRESS. · EST. 2026 ·" />

      <div className="max-w-[1400px] mx-auto px-5 md:px-14 -mt-6 relative z-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        {STATS.map(([n, l]) => (
          <div key={l} data-testid={`stat-${l.toLowerCase().split(' ')[0]}`} className="rounded-2xl bg-cream border-2 border-cocoa p-5 text-center shadow-[5px_5px_0_#2A1010]">
            <p className="font-display text-5xl text-cocoa leading-none">{n}</p>
            <p className="font-cond text-[10px] tracking-[0.3em] text-cocoa/60 mt-1">{l}</p>
          </div>
        ))}
      </div>

      <Section id="problem" kicker="WHY WE EXIST" title="THE PROBLEM. THE SOLUTION.">
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <motion.div data-testid="problem-card" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="rounded-[2rem] bg-maroon text-cream border-2 border-cocoa p-8 md:p-10">
            <p className="font-cond text-sm tracking-[0.35em] text-kpink">THE PROBLEM</p>
            <h3 className="font-display text-5xl md:text-6xl leading-none mt-2">SNACKS MADE YOU<br />CHOOSE.</h3>
            <p className="text-cream/80 mt-3">Tasty <i>or</i> healthy. Fun <i>or</i> functional. Never both.</p>
            <ul className="mt-6 space-y-3">
              {PROBLEMS.map((p) => <li key={p} className="flex gap-3 text-sm"><span className="w-6 h-6 rounded-full bg-kred flex items-center justify-center shrink-0"><X size={13} /></span>{p}</li>)}
            </ul>
          </motion.div>
          <motion.div data-testid="solution-card" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="rounded-[2rem] bg-kyellow text-cocoa border-2 border-cocoa p-8 md:p-10">
            <p className="font-cond text-sm tracking-[0.35em] text-maroon">THE SOLUTION</p>
            <h3 className="font-display text-5xl md:text-6xl leading-none mt-2">PROKRISP.<br />BOTH. ALWAYS.</h3>
            <p className="text-cocoa/80 mt-3">A chip engineered like a supplement and designed like match-day kit.</p>
            <ul className="mt-6 space-y-3">
              {SOLUTIONS.map((s) => <li key={s} className="flex gap-3 text-sm font-medium"><span className="w-6 h-6 rounded-full bg-cocoa text-kyellow flex items-center justify-center shrink-0"><Check size={13} /></span>{s}</li>)}
            </ul>
          </motion.div>
        </div>
        <div className="flex flex-wrap justify-center gap-8 mt-12">
          {FLAVOR_LIST.map((f, i) => (
            <img key={f.slug} src={f.img100} alt={f.name} loading="lazy" className="h-72 object-contain gpu chip-drift" style={{ '--t': `${7 + i}s`, '--r': i ? '4deg' : '-4deg' }} />
          ))}
        </div>
      </Section>

      <Section id="team" kicker="THE HUMANS" title="SIX PEOPLE. ONE CRUNCH." className="!pt-4">
        <p className="text-sm text-cocoa/70 mt-3 max-w-xl">A tiny team of athletes, food nerds, designers and community kids. Everyone here has eaten more test batches than they’ll admit.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {TEAM.map((m, i) => (
            <motion.div key={m.who} data-testid={`team-member-${i + 1}`} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
              className="card-pop rounded-[2rem] bg-white/70 border-2 border-cocoa p-6 flex flex-col">
              <div className="w-24 h-24 rounded-full border-2 border-cocoa flex items-center justify-center font-display text-4xl text-cocoa" style={{ background: m.color }}>
                {m.who.split(' ').map((w) => w[0]).join('')}
              </div>
              <p className="font-display text-3xl text-cocoa mt-5 leading-none">{m.who}</p>
              <p className="font-cond text-xs tracking-[0.3em] mt-1" style={{ color: m.color === '#F7B500' ? '#7A1E1E' : m.color }}>{m.name}</p>
              <p className="text-sm text-cocoa/70 mt-3 flex-1">{m.bio}</p>
              <div className="flex gap-2 mt-4">
                <span className="w-9 h-9 rounded-full border-2 border-cocoa/20 flex items-center justify-center text-cocoa/60"><Instagram size={15} /></span>
                <span className="w-9 h-9 rounded-full border-2 border-cocoa/20 flex items-center justify-center text-cocoa/60"><Mail size={15} /></span>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      <section className="bg-cocoa text-cream rounded-[2.5rem] mx-3 md:mx-8 px-6 md:px-14 py-14">
        <div className="max-w-[1300px] mx-auto">
          <p className="font-cond text-xs tracking-[0.4em] text-kyellow">WHAT WE STAND FOR</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
            {VALUES.map(([t, d]) => (
              <div key={t} data-testid={`value-${t.toLowerCase().replace(/\s|-/g, '')}`}>
                <p className="font-display text-5xl text-kyellow leading-none">{t}</p>
                <p className="text-sm text-cream/70 mt-2">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Section id="contact" kicker="SAY HI" title="TALK TO THE CRUNCH.">
        <div className="mt-8 rounded-[2rem] overflow-hidden border-2 border-cocoa shadow-[8px_8px_0_#E11D24]">
          <img src={ASSETS.muralTop} alt="PROKRISP — GO BEYOND" className="w-full object-cover" data-testid="contact-mural" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
          {CONTACTS.map((c) => (
            <a key={c.label} href={c.href} data-testid={`contact-${c.label.toLowerCase().split(' ')[0]}`} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
              className="card-pop rounded-2xl bg-cocoa text-cream border-2 border-cocoa p-4">
              <p className="font-cond text-[9px] tracking-[0.3em] text-kyellow">{c.label}</p>
              <p className="font-bold text-sm mt-1 break-all">{c.value}</p>
            </a>
          ))}
        </div>
        <div className="rounded-[2rem] bg-kyellow border-2 border-cocoa p-8 mt-6 flex flex-col sm:flex-row items-center justify-between gap-5">
          <p className="font-display text-4xl text-cocoa leading-none">WANT FIRST CRUNCH?</p>
          <KrispButton testid="about-join-waitlist" variant="dark" onClick={() => openWaitlist('THE FULL DROP')}>JOIN THE WAITLIST →</KrispButton>
        </div>
      </Section>

      <Footer />
    </main>
  );
}
